import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { dashboardAPI } from '../services/api';
import { useToast } from '../components/Toast';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend
} from 'recharts';
import { TrendingUp, Star, RefreshCw } from 'lucide-react';

const PERIODS = ['Last 7 days', 'Last 30 days', 'Last 3 months', 'Last 6 month'];
const PIE_COLORS = ['#fbbf24', '#6c63ff', '#38bdf8', '#22d3a0', '#ff5f7e'];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => String(currentYear - i));

const CustomTooltipArea = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 14px'
      }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4 }}>{label}</p>
        <p style={{ color: 'var(--primary-dark)', fontSize: 14, fontWeight: 700 }}>
          ₹{Number(payload[0].value).toLocaleString('en-IN')}
        </p>
      </div>
    );
  }
  return null;
};

CustomTooltipArea.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number })
  ),
  label: PropTypes.string,
};

CustomTooltipArea.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [period, setPeriod] = useState('Last 7 days');
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = async (p, year = null) => {
    setLoading(true);
    try {
      const param = year ? `Year ${year}` : p;
      const [analyticsRes, topRes] = await Promise.all([
        dashboardAPI.getAnalytics(param),
        dashboardAPI.getTopSellers(10),
      ]);
      setData(analyticsRes.data);
      setTopSellers(topRes.data || []);
    } catch {
      toast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period, selectedYear); }, [period, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordersByStatus = data?.ordersByStatus;
  const pieData = ordersByStatus ? [
    { name: 'New', value: Number(ordersByStatus.pending || 0) },
    { name: 'Ready', value: Number(ordersByStatus.processing || 0) },
    { name: 'Out for Delivery', value: Number(ordersByStatus.shipped || 0) },
    { name: 'Delivered', value: Number(ordersByStatus.delivered || 0) },
    { name: 'Cancelled', value: Number(ordersByStatus.cancelled || 0) },
  ].filter(d => d.value > 0) : [];

  const totalRevenue = (data?.revenueChart || []).reduce((s, d) => s + Number(d.revenue || 0), 0);

  // Extract nested ternary for delivery rate
  const deliveryRate = (() => {
    if (!ordersByStatus?.total) return '—';
    return `${Math.round((Number(ordersByStatus.delivered || 0) / Number(ordersByStatus.total)) * 100)}%`;
  })();

  // Extract nested ternary for total orders display
  const totalOrdersDisplay = ordersByStatus?.total ?? '—';

  const summaryCards = [
    { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString('en-IN')}`, color: 'var(--green)', bg: 'var(--green-bg)' },
    { label: 'Total Orders', value: totalOrdersDisplay, color: 'var(--primary-dark)', bg: 'var(--primary-glow-sm)' },
    { label: 'Delivery Rate', value: deliveryRate, color: 'var(--blue)', bg: 'var(--blue-bg)' },
  ];

  return (
    <Layout title="Analytics" subtitle="Revenue trends and performance insights">

      {/* ── Period picker ── */}
      <div style={{ marginBottom: 20 }}>

        {/* Tabs: 2×2 grid on mobile, single row on desktop */}
        <div className="analytics-period-tabs">
          {PERIODS.map(p => (
            <button
              key={p}
              onClick={() => { setSelectedYear(null); setPeriod(p); }}
              className={`analytics-period-btn${period === p && !selectedYear ? ' analytics-period-btn--active' : ''}`}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Year + Refresh row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <select
            value={selectedYear || ''}
            onChange={e => setSelectedYear(e.target.value || null)}
            style={{
              flex: 1,
              height: 38,
              borderRadius: 8,
              border: selectedYear ? '2px solid var(--primary-dark)' : '1px solid var(--border)',
              background: selectedYear ? 'var(--primary-glow-sm)' : 'var(--bg-card)',
              color: selectedYear ? 'var(--primary-dark)' : 'var(--text-secondary)',
              fontSize: 13,
              fontWeight: selectedYear ? 700 : 500,
              cursor: 'pointer',
              outline: 'none',
              padding: '0 10px',
            }}
          >
            <option value="">By Year...</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <button
            className="btn btn-ghost btn-sm"
            onClick={() => load(period, selectedYear)}
            style={{ flexShrink: 0, height: 38, padding: '0 14px' }}
          >
            <RefreshCw size={13} />
            <span style={{ marginLeft: 4 }}>Refresh</span>
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3,1fr)',
        gap: 12,
        marginBottom: 20,
      }}>
        {summaryCards.map(card => (
          <div key={card.label} className="card" style={{ textAlign: 'center', padding: '18px 10px' }}>
            <div style={{ fontSize: 'clamp(18px, 4vw, 28px)', fontWeight: 800, color: card.color, letterSpacing: -1, marginBottom: 5, lineHeight: 1.1 }}>
              {card.value}
            </div>
            <div style={{ fontSize: 'clamp(10px, 2.5vw, 12px)', color: 'var(--text-muted)', fontWeight: 500 }}>
              {card.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Revenue Area Chart ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Revenue Trend</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--green)' }}>
            <TrendingUp size={13} /> {selectedYear ? `Year ${selectedYear}` : period}
          </span>
        </div>
        {loading ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.revenueChart || []}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#6c63ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="dayLabel"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltipArea />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#6c63ff"
                strokeWidth={2.5}
                fill="url(#areaGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Pie + Top Sellers ── */}
      <div className="analytics-bottom-grid">

        {/* Order distribution pie */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Order Distribution</span>
          </div>
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : pieData.length === 0 ? (
            <div className="empty-state"><p>No order data</p></div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => v.toLocaleString()}
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, color: 'var(--text-secondary)', paddingTop: 6 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Sellers */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Sellers by Revenue</span>
          </div>
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : topSellers.length === 0 ? (
            <div className="empty-state"><p>No seller data</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topSellers.slice(0, 8).map((seller, i) => {
                const maxRev = Math.max(...topSellers.map(s => Number(s.totalRevenue || 0)));
                const pct = maxRev > 0 ? (Number(seller.totalRevenue || 0) / maxRev) * 100 : 0;
                return (
                  <div key={seller.sellerId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', width: 16, textAlign: 'right', flexShrink: 0 }}>
                      {i + 1}
                    </span>
                    <div className="avatar" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
                      {seller.profilePicture
                        ? <img src={seller.profilePicture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : seller.businessName?.[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, gap: 6 }}>
                        <span style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0,
                        }}>
                          {seller.businessName}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', flexShrink: 0 }}>
                          ₹{Number(seller.totalRevenue).toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${pct}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      </div>
                    </div>
                    <div className="star-rating" style={{ fontSize: 11, flexShrink: 0 }}>
                      <Star size={10} fill="currentColor" /> {seller.rating?.toFixed(1) ?? '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Inline responsive styles */}
      <style>{`
        .analytics-period-tabs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          width: 100%;
        }
        .analytics-period-btn {
          padding: 9px 10px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 10px;
          border: 1.5px solid var(--border);
          cursor: pointer;
          font-family: var(--font);
          white-space: nowrap;
          text-align: center;
          transition: all 0.18s;
          background: var(--bg-card);
          color: var(--text-muted);
          width: 100%;
        }
        .analytics-period-btn--active {
          background: var(--primary);
          color: #fff;
          border-color: var(--primary);
          box-shadow: var(--shadow-g);
        }
        @media (min-width: 1024px) {
          .analytics-period-tabs {
            display: flex;
            flex-wrap: nowrap;
            gap: 3px;
            background: var(--bg-hover);
            padding: 4px;
            border-radius: 10px;
            border: 1px solid var(--border);
            width: fit-content;
          }
          .analytics-period-btn {
            border: none;
            background: transparent;
            border-radius: 7px;
            padding: 7px 14px;
            font-size: 13px;
          }
          .analytics-period-btn--active {
            background: var(--primary);
            border: none;
          }
        }
        .analytics-bottom-grid {
          display: grid;
          grid-template-columns: 1fr 1.4fr;
          gap: 16px;
        }
        @media (max-width: 1023px) {
          .analytics-bottom-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }
      `}</style>
    </Layout>
  );
}
